import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, ShieldCheck, Users } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ImportExportLinks } from '@/features/imports-exports';
import { useStore } from '@/shared/store/store-context';

import { CreateCustomerDialog } from '../components/create-customer-dialog';
import { useCustomerColumns } from '../components/customer-columns';
import { customerFilters } from '../components/customer-filters';
import { useCustomers } from '../hooks/use-customers';
import { customerName } from '../schemas/customer-rules';

const ROUTE_ID = '/_authed/_active/s/$storeId/customers/' as const;
const ROUTE_PATH = '/s/$storeId/customers/' as const;

function Customers() {
  const { t } = useTranslation('customers');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const [creating, setCreating] = useState(false);

  const filters = useMemo(
    () => customerFilters({ t, currency: store.defaultCurrency }),
    [store.defaultCurrency, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useCustomers(store.id, list.params);
  const columns = useCustomerColumns(store.id, store.defaultCurrency);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'customers',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;
  const open = (customerId: string) =>
    void navigate({
      to: '/s/$storeId/customers/$customerId',
      params: { storeId: store.id, customerId },
    });

  const addButton = (tour?: boolean) => (
    <Can permission="customers.write">
      <Button onClick={() => setCreating(true)} data-tour={tour ? 'customers.list.add' : undefined}>
        <Plus aria-hidden />
        {t('create.action')}
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        actions={
          <>
            <Button asChild variant="outline" data-tour="customers.list.privacy-link">
              <Link to="/s/$storeId/privacy-requests" params={{ storeId: store.id }}>
                <ShieldCheck aria-hidden />
                {t('privacy.link')}
              </Link>
            </Button>
            <div data-tour="customers.list.import-export" className="flex items-center gap-2">
              <ImportExportLinks storeId={store.id} kind="customers_csv" />
            </div>
            {addButton(true)}
          </>
        }
      />
      {isEmptyList ? (
        <EmptyState
          icon={Users}
          title={t('empty.title')}
          description={t('empty.description')}
          tour="customers.list.empty"
          tourLink
          action={addButton()}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) => open(row.id)}
          getRowLabel={(row) => customerName(row) ?? row.email}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'customers.list.table', search: 'customers.list.search' }}
        />
      )}
      {creating ? (
        <CreateCustomerDialog
          storeId={store.id}
          open
          onOpenChange={setCreating}
          onCreated={(customer) => open(customer.id)}
        />
      ) : null}
    </PageContent>
  );
}

export function CustomersPage() {
  return (
    <FeatureErrorBoundary name="customers.list">
      <Customers />
    </FeatureErrorBoundary>
  );
}
